import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Paper,
  Fade,
  Zoom,
} from "@mui/material";
import { Person, Lock, Login } from "@mui/icons-material";
import { login as loginAction } from "../../store/slices/authSlice";
import { useForm } from "../../hooks";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDButton from "../../components/MDButton";

const AuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector(
    (state) => state.auth,
  );
  const { values, handleChange, resetForm } = useForm({
    username: "",
    password: "",
  });

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await dispatch(loginAction(values));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  return (
    <MDBox
      position="absolute"
      width="100%"
      minHeight="100vh"
      sx={{
        background:
          "url('https://images.unsplash.com/photo-1582407947304-fd86f028f716?q=80&w=2592&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        boxShadow: "none",
        border: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
          zIndex: 1,
        },
      }}
    >
      <Fade in={true} timeout={1000}>
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            maxWidth: 450,
            width: "100%",
            margin: 2,
          }}
        >
          <Zoom in={true} timeout={800}>
            <Card
              elevation={12}
              sx={{
                borderRadius: 4,
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <CardContent sx={{ p: 5 }}>
                <MDBox textAlign="center" mb={4}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 80,
                      height: 80,
                      mx: "auto",
                      mb: 3,
                      boxShadow: 3,
                    }}
                  >
                    <Login sx={{ fontSize: 40, color: "white" }} />
                  </Avatar>
                  <MDTypography
                    variant="h3"
                    fontWeight="bold"
                    color="dark"
                    mb={1}
                  >
                    Willkommen zurück
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    color="text"
                    sx={{ opacity: 0.8 }}
                  >
                    Geben Sie Ihren Benutzernamen oder E-Mail und Ihr Passwort
                    ein, um sich anzumelden.
                  </MDTypography>
                </MDBox>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      boxShadow: 1,
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <MDBox component="form" role="form" onSubmit={handleSubmit}>
                  <MDBox mb={3}>
                    <TextField
                      fullWidth
                      label="Benutzername oder E-Mail"
                      type="text"
                      name="username"
                      value={values.username}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ color: "action.active", mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 2,
                          },
                          "&.Mui-focused": {
                            boxShadow: 3,
                          },
                        },
                      }}
                    />
                  </MDBox>

                  <MDBox mb={3}>
                    <TextField
                      fullWidth
                      label="Passwort"
                      type="password"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Lock sx={{ color: "action.active", mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 2,
                          },
                          "&.Mui-focused": {
                            boxShadow: 3,
                          },
                        },
                      }}
                    />
                  </MDBox>

                  <MDBox mt={4} mb={2}>
                    <MDButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={loading}
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        py: 2,
                        borderRadius: 3,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        boxShadow: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                          boxShadow: 6,
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: "grey",
                          transform: "none",
                        },
                      }}
                    >
                      {loading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} color="inherit" />
                          <span>Anmelden...</span>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Login />
                          <span>Anmelden</span>
                        </Box>
                      )}
                    </MDButton>
                  </MDBox>

                  <MDBox mt={4} textAlign="center">
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Hinweis:</strong> Benötigen Sie Hilfe? Wenden
                        Sie sich an Ihren Administrator.
                      </MDTypography>
                    </Paper>
                  </MDBox>
                </MDBox>
              </CardContent>
            </Card>
          </Zoom>
        </Box>
      </Fade>
    </MDBox>
  );
};

export default AuthPage;
